import React, { useState } from 'react';
import { 
  Menu, 
  MenuItem, 
  Button, 
  Snackbar, 
  alpha,
  Box
} from '@mui/material';
import { 
  Link2, 
  Share2, 
  Facebook, 
  Twitter, 
  Instagram,
  ChevronRight
} from 'lucide-react';
import { motion } from 'framer-motion';
import { trackShared } from '../../utils/analytics';

// Custom Reddit icon since Lucide React doesn't have one
const RedditIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="9" fill="#FF4500" />
    <circle cx="8" cy="10" r="1.5" fill="white" />
    <circle cx="16" cy="10" r="1.5" fill="white" />
    <path d="M8.5,15.5 C10.5,17.5 13.5,17.5 15.5,15.5" stroke="white" strokeLinecap="round" />
    <path d="M18.5,11c0.83,0,1.5-0.67,1.5-1.5s-0.67-1.5-1.5-1.5" stroke="#FF4500" />
    <path d="M18.5,8.5C18.5,6.57,16.93,5,15,5" stroke="#FF4500" />
    <path d="M5.5,11c-0.83,0-1.5-0.67-1.5-1.5s0.67-1.5,1.5-1.5" stroke="#FF4500" />
  </svg>
);

const ModernShareButton = ({ 
  circle,
  darkMode = false,
  buttonText = "Share Circle & Unlock Savings",
  fullWidth = true,
  size = "large" // large, medium, small
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  
  // Define colors for dark mode
  const colors = {
    primary: darkMode ? '#818cf8' : '#6366F1',
    primaryHover: darkMode ? '#4F46E5' : '#4F46E5',
    primaryDark: darkMode ? '#4338CA' : '#4338CA',
    secondary: darkMode ? '#38bdf8' : '#0EA5E9',
    paper: darkMode ? '#1F2937' : 'white',
    paperBorder: darkMode ? 'rgba(75, 85, 99, 0.3)' : 'rgba(0, 0, 0, 0.08)',
    menuItemHover: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
    text: darkMode ? 'rgba(255, 255, 255, 0.9)' : 'white',
    textSecondary: darkMode ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.7)',
    shadow: darkMode ? '0 8px 25px rgba(79, 70, 229, 0.3)' : '0 8px 25px rgba(79, 70, 229, 0.4)',
    shadowHover: darkMode ? '0 12px 30px rgba(79, 70, 229, 0.4)' : '0 12px 30px rgba(79, 70, 229, 0.5)',
    snackbar: darkMode ? '#374151' : undefined,
    snackbarText: darkMode ? 'rgba(255, 255, 255, 0.9)' : undefined
  };

  // Button size variations
  const sizeStyles = {
    large: {
      py: 2,
      px: 3,
      fontSize: '18px',
      borderRadius: 3,
      iconSize: 20
    },
    medium: {
      py: 1.5,
      px: 2.5,
      fontSize: '16px',
      borderRadius: 2.5,
      iconSize: 18
    },
    small: {
      py: 1,
      px: 2,
      fontSize: '14px',
      borderRadius: 2,
      iconSize: 16
    }
  };
  
  const currentSize = sizeStyles[size] || sizeStyles.large;
  
  // Format sharing content with null checks
  const formatShareContent = () => {
    if (!circle) return null;
    
    const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;
    const circleId = circle?.id || '';
    const shareUrl = `${baseUrl}/circle/${circleId}`;
    
    // Format features text if available
    const featuresText = circle.features?.length > 0 
      ? ` Features: ${Array.isArray(circle.features) 
          ? circle.features.map(f => typeof f === 'string' ? f : f.short_description).join(', ')
          : ''}`
      : '';
    
    return {
      url: shareUrl,
      title: circle.name ? `Join ${circle.name} - ${circle.carrier || 'Mobile'} Circle` : `Join our ${circle.carrier || 'Mobile'} Circle`,
      description: `Join ${circle.name || 'our'} Circle! Save money on your ${circle.carrier || 'mobile'} plan. $${circle.monthlyPayment || 0}/month, ${circle.available_slots || 0} spots left.${featuresText}`,
      features: featuresText
    };
  };
  
  const shareContent = formatShareContent();
  
  const handleOpenMenu = (event) => {
    if (!circle) {
      console.error('Missing required data for sharing');
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };
  
  const handleCopyLink = async () => {
    try {
      if (!shareContent) {
        console.error('Share content not available');
        return;
      }
      
      const textToCopy = `${shareContent.title}\n${shareContent.description}\n\nJoin here: ${shareContent.url}`;
      await navigator.clipboard.writeText(textToCopy);
      trackShared(circle?.id, 'copy_link');
      setShowCopySuccess(true);
      handleCloseMenu();
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };
  
  const handleSocialShare = (platform) => {
    if (!shareContent) {
      console.error('Share content not available');
      return;
    }
    
    let platformUrl;
    switch (platform) {
      case 'facebook':
        platformUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareContent.url)}&quote=${encodeURIComponent(`${shareContent.title}\n${shareContent.description}`)}`;
        break;
      case 'twitter':
        // Twitter has a character limit, so we'll use a shorter format
        const twitterText = `${shareContent.title}\n${shareContent.description}`;
        platformUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(shareContent.url)}`;
        break;
      case 'reddit':
        platformUrl = `https://www.reddit.com/submit?url=${encodeURIComponent(shareContent.url)}&title=${encodeURIComponent(shareContent.title)}`;
        break;
      case 'instagram':
        handleCopyLink();
        alert('Link copied! You can now share it on Instagram');
        handleCloseMenu();
        return;
      default:
        console.error('Unsupported platform:', platform);
        return;
    }
    
    // Open share window in the center of the screen
    const width = 600;
    const height = 400;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    trackShared(circle?.id, platform);
    window.open(
      platformUrl,
      'share-dialog',
      `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no`
    );
    handleCloseMenu();
  };
  
  return (
    <>
      {/* Modern animated button with gradient and effects */}
      <Button
        component={motion.button}
        fullWidth={fullWidth}
        onClick={handleOpenMenu}
        sx={{
          py: currentSize.py,
          px: currentSize.px,
          borderRadius: currentSize.borderRadius,
          background: darkMode
            ? `linear-gradient(90deg, ${colors.primary}, ${colors.primaryDark})`
            : `linear-gradient(90deg, rgb(79, 70, 229), rgb(124, 58, 237))`,
          color: colors.text,
          fontWeight: 500,
          fontSize: currentSize.fontSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
          textTransform: 'none',
          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
          '&:hover': {
            background: darkMode
              ? `linear-gradient(90deg, ${colors.primary}, ${colors.primaryDark})`
              : `linear-gradient(90deg, rgb(79, 70, 229), rgb(124, 58, 237))`
          }
        }}
        whileHover={{ 
          scale: 1.02,
          boxShadow: colors.shadowHover
        }}
        whileTap={{ scale: 0.98 }}
      >
        {/* Shine effect */}
        <Box
          component={motion.div}
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
            width: '30%',
            opacity: 0.2,
            pointerEvents: 'none'
          }}
          animate={{ 
            x: ['-100%', '200%']
          }}
          transition={{ 
            duration: 3,
            repeat: Infinity,
            repeatDelay: 1
          }}
          style={{ 
            skewX: -15
          }}
        />
        
        <Share2 size={currentSize.iconSize} style={{ marginRight: 8 }} />
        <span>{buttonText}</span>
        
        {/* Animated arrow */}
        <Box
          component={motion.div}
          animate={{
            x: [0, 5, 0]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            repeatType: "mirror"
          }}
          sx={{ ml: 1 }}
        >
          <ChevronRight size={currentSize.iconSize} />
        </Box>
      </Button>
      
      {/* Sharing menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          elevation: darkMode ? 4 : 3,
          sx: {
            borderRadius: '12px',
            mt: 1,
            minWidth: '200px',
            bgcolor: colors.paper,
            border: `1px solid ${colors.paperBorder}`,
            color: colors.textSecondary,
            '& .MuiMenuItem-root': {
              '&:hover': {
                bgcolor: colors.menuItemHover
              }
            }
          }
        }}
      >
        <MenuItem onClick={handleCopyLink} sx={{ gap: 1.5, py: 1 }}>
          <Link2 size={18} />
          Copy Link
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('facebook')} sx={{ gap: 1.5, py: 1 }}>
          <Facebook size={18} color="#1877F2" />
          Share on Facebook
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('twitter')} sx={{ gap: 1.5, py: 1 }}>
          <Twitter size={18} color="#1DA1F2" />
          Share on Twitter
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('reddit')} sx={{ gap: 1.5, py: 1 }}>
          <div style={{ color: '#FF4500' }}>
            <RedditIcon />
          </div>
          Share on Reddit
        </MenuItem>
        <MenuItem onClick={() => handleSocialShare('instagram')} sx={{ gap: 1.5, py: 1 }}>
          <Instagram size={18} color="#E4405F" />
          Share on Instagram
        </MenuItem>
      </Menu>
      
      {/* Success notification */}
      <Snackbar
        open={showCopySuccess}
        autoHideDuration={2000}
        onClose={() => setShowCopySuccess(false)}
        message="Link copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            bgcolor: colors.snackbar,
            color: colors.snackbarText
          }
        }}
      />
    </>
  );
};

export default ModernShareButton;